import { Component, type ErrorInfo, type ReactNode } from "react";
import errorImage from "#assets/error.webp";
import Box from "#components/Box.tsx";
import { Button } from "#components/Button.tsx";
import { ThemeButtonVariant } from "#components/theme.ts";

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child
 * component tree and displays a fallback UI instead of crashing the entire app.
 */
export default class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		console.error("ErrorBoundary caught an error:", error, errorInfo);
	}

	handleRefresh = (): void => {
		window.location.reload();
	};

	render(): ReactNode {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}

			return (
				<main className="relative h-screen w-screen flex flex-col items-center justify-start z-1">
					<img
						src={errorImage}
						alt="Error Background"
						className="absolute top-0 w-screen h-screen object-cover z-2"
					/>
					<Box className="z-3 max-w-3xl backdrop-blur-3xl bg-slate-800/85 pt-5 mt-9 mx-3">
						<h1 className="text-4xl font-heading mb-3">Something went wrong</h1>
						<p className="mb-2">
							We encountered an unexpected error while rendering this page.
							Please try refreshing the page.
						</p>
						<p className="text-sm text-white/60 mb-4">
							If the problem persists, please contact support or try again
							later.
						</p>
						<div className="flex flex-row gap-3">
							<Button
								onClick={this.handleRefresh}
								className="mb-3"
								variant={ThemeButtonVariant.Solid}
							>
								Refresh Page
							</Button>
							<Button
								href="/dashboard"
								className="mb-3"
								variant={ThemeButtonVariant.Outline}
							>
								Return Home
							</Button>
						</div>
					</Box>
				</main>
			);
		}

		return this.props.children;
	}
}
